import { useEffect, useMemo, useRef, useState } from "react"
import "../styles/ChatMobile.css"
import { Drawer, DrawerRef } from "../../../components/drawers/Drawer"
import { useGlobal, useHub } from "../../../core/Global"
import { API_ROUTES } from "../../../core/Constants"
import ChatContactItem from "../models/ChatContactItem"
import ChatMobileRow from "./ChatMobileRow"
import Indicator from "../../../components/base/Indicator"
import Icon from "../../../components/base/Icon"
import { CHAT_BLOCK_CHANGED_EVENT } from "./ChatContactContextMenu"

const ARCHIVED_PAGE_SIZE = 100

interface IProps {
    readedChatId?: string
    onClose: () => void
}

export default function ArchivedChatsDrawer(props: IProps) {

    const hub = useHub()
    const [globalState] = useGlobal()
    const drawer = useRef<DrawerRef>(null)
    const sentinelRef = useRef<HTMLDivElement>(null)

    const [archived, archivedSet] = useState<ChatContactItem[]>()
    const [loading, loadingSet] = useState<boolean>(true)

    // Archived chats are paged (fixed 100 per page) and appended as the user scrolls down.
    const [page, pageSet] = useState<number>(1)
    const [hasMore, hasMoreSet] = useState<boolean>(false)
    const [loadingMore, loadingMoreSet] = useState<boolean>(false)

    // Refs mirror the paging state so the IntersectionObserver callback always reads fresh values.
    const pageRef = useRef<number>(1)
    const hasMoreRef = useRef<boolean>(false)
    const loadingMoreRef = useRef<boolean>(false)

    // Blocked chats are archived too, but listed apart behind a thin "Blocked" row at the top.
    const [blockedMode, blockedModeSet] = useState<boolean>(false)
    const [blockedCount, blockedCountSet] = useState<number>(0)
    const blockedModeRef = useRef<boolean>(false)

    useEffect(() => {
        const loadCount = () => hub.Get(API_ROUTES.Chat + "blockedcount", res => {
            if (res.success) blockedCountSet(res.data ?? 0)
        })
        const onChange = (e: Event) => {
            // Unblocked: the chat is back in the live list, so that is where we go too. Closed by
            // script so the drawer takes its backdrop with it; onClose then unmounts it.
            if ((e as CustomEvent).detail?.blocked === false) drawer.current?.close()
            else loadCount()
        }
        loadCount()
        window.addEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange)
        return () => window.removeEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange)
    }, [])

    const loadPage = (p: number) => {
        hub.Get(API_ROUTES.Chat + (blockedModeRef.current ? "blocked" : "archived") + "?page=" + p, res => {
            const data: ChatContactItem[] = res.data ?? []
            if (p <= 1) archivedSet(data)
            else archivedSet(prev => [...(prev ?? []), ...data])

            pageRef.current = p
            pageSet(p)

            const more = data.length >= ARCHIVED_PAGE_SIZE
            hasMoreRef.current = more
            hasMoreSet(more)

            loadingMoreRef.current = false
            loadingMoreSet(false)
            loadingSet(false)
        }, () => {
            loadingMoreRef.current = false
            loadingMoreSet(false)
            loadingSet(false)
        })
    }

    useEffect(() => {
        loadPage(1)
    }, [])

    const switchList = (blocked: boolean) => {
        blockedModeRef.current = blocked
        blockedModeSet(blocked)
        hasMoreRef.current = false
        hasMoreSet(false)
        loadingSet(true)
        loadPage(1)
    }

    const loadMore = () => {
        if (!hasMoreRef.current || loadingMoreRef.current) return
        loadingMoreRef.current = true
        loadingMoreSet(true)
        loadPage(pageRef.current + 1)
    }

    useEffect(() => {
        const el = sentinelRef.current
        if (!el) return
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) loadMore()
        }, { rootMargin: "200px" })
        observer.observe(el)
        return () => observer.disconnect()
    }, [archived])

    const items = useMemo(() => {
        // A chat that gets a new message leaves the archive server-side and is pushed into the live
        // chat list, so drop it here instead of leaving a stale row in the archived drawer.
        const base = (archived ?? []).filter(f => !globalState.chatlist?.some(c => c.chatId == f.chatId))
        return [...base].sort((a, b) => {
            if (a.pinned && !b.pinned) return -1
            if (!a.pinned && b.pinned) return 1
            const dateA = new Date(a.lastUpdateDate!).getTime()
            const dateB = new Date(b.lastUpdateDate!).getTime()
            return dateB - dateA
        })
    }, [archived, globalState.chatlist])

    return (
        <Drawer ref={drawer} title={blockedMode ? "Spam" : "Archived Chats"} titleIcon={blockedMode ? "ban" : "box-archive"}
            noPadding onClose={props.onClose}>
            <div className="chat-mobile-list">
                {
                    (blockedMode || blockedCount > 0) &&
                    <a href="#" className="d-flex align-items-center px-4 py-3 border-bottom text-gray-700 bg-light fs-7"
                        onClick={e => {
                            e.preventDefault()
                            switchList(!blockedMode)
                        }}>
                        <Icon icon={blockedMode ? "arrow-left" : "ban"} className="me-2" size={12} />
                        <span className="fw-bold">{blockedMode ? "Back to archived" : "Spam"}</span>
                        <span className="badge badge-light-dark ms-2">{blockedCount}</span>
                        {!blockedMode && <Icon icon="chevron-right" className="ms-auto" size={10} />}
                    </a>
                }
                {
                    loading ?
                        <div className="pt-10 text-center">
                            <Indicator />
                        </div>
                        : items.length == 0 ?
                            <div className="chat-mobile-empty">{blockedMode ? "No spam chats." : "No archived chats."}</div>
                            :
                            <>
                                {
                                    items.map((item, index) =>
                                        <ChatMobileRow key={item.id} item={item}
                                            seperator={index + 1 < items.length}
                                            readed={item.chatId == props.readedChatId} />
                                    )
                                }
                                {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
                                {loadingMore &&
                                    <div className="py-4 text-center">
                                        <Indicator />
                                    </div>
                                }
                            </>
                }
            </div>
        </Drawer>
    )
}
